import { CountryCodeList } from 'types/countries';
import configJSON from 'config.json';
import { formatNumberDown } from '@zoocasa/node-kit/numbers/format-number';
import { AgentObservabilityService, createRecaptchaError } from 'observability/services/agent-observability';
import { ApiTimeouts } from 'constants/timeouts';
import { RECAPTCHA_MAX_RETRIES } from 'constants/retry';

export interface AgentLicense {
  locale: string;
  number: string;
  state: string;
  primary: boolean;
}

export interface AgentLocation {
  city: string;
  state: string;
  zipcode: string;
}

export interface AgentFull {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  photo: string;
  preferredName: string;
  title: string;
  bio: string;
  languages: string[];
  specializations: string[];
  designations: string[];
  certifications: string[];
  facebook: string;
  instagram: string;
  linkedIn: string;
  twitter: string;
  website: string;
  youtube: string;
  tiktok: string;
  city: string;
  state: string;
  zipcode: string;
  countryCode: string;
  asab: string[];
  stateBroker: string[];
  activeLocations: AgentLocation[];
  license: AgentLicense[];
  isOffboarding: boolean;
  milnet: {
    milnetAssociation: string;
    status: string;
  };
}

export interface AgentList {
  id: string;
  firstName: string;
  lastName: string;
  city: string;
  state: string;
  photo: string;
  email: string;
  phoneNumber: string;
  bio: string;
  license?: AgentLicense[];
}

interface LocationSuggestionsResponse {
  locationSuggestions: string[];
}

interface NameSuggestionsResponse {
  nameSuggestions: string[];
}

export interface AgentsResponseBody {
  count: number;
  agents: AgentList[];
}

interface SearchAgentsResponse {
  search: AgentsResponseBody;
}

interface FeaturedAgentsResponse {
  featuredAgents: AgentsResponseBody;
}

interface FullAgentResponse {
  agent: AgentFull;
}

interface AgentEndpointParams {
  query: string;
  headers?: Record<string, string> | null;
}

/**
 * Agent count response body
 * @param {number} total - total number of agents
 * @param {Object} countries - count of agents by country
 * @param {number} countries.ca - count of agents in Canada
 * @param {number} countries.us - count of agents in US
 */
export interface AgentCountBody {
  total: number;
  countries: {
    ca: number;
    us: number;
  };
}

interface AgentCountResponse {
  agentCounts: AgentCountBody;
}

export interface AgentFilters {
  isMilnet: boolean;
}

enum SearchType {
  AGENT = 'AGENT',
  BROKER = 'BROKER'
}

export enum AgentType {
  RESIDENTIAL = 'RESIDENTIAL',
  COMMERCIAL = 'COMMERCIAL'
}

export enum SortType {
  FirstName = 'firstName',
  LastName = 'lastName',
  Random = 'random'
}

enum TokenActionType {
  AGENT_SEARCH = 'AGENT_SEARCH',
  VIEW_AGENT = 'VIEW_AGENT',
  FEATURED_AGENTS = 'FEATURED_AGENTS',
  NAME_SUGGESTIONS = 'NAME_SUGGESTIONS'
}

export const PAGE_SIZE = 12;
export const AGENT_SEARCH_PATH = '/agents-search';
export const EXP_COMMERCIAL_PATH = '/experts';

interface BuildSearchAgentsQueryParams {
  name?: string;
  location?: string;
  country: string;
  page: number;
  agentType: string;
  isMilnet: boolean;
  recaptchaToken: string;
}

export const buildSearchAgentsQuery = ({ name, location, country, page, agentType, isMilnet, recaptchaToken }: BuildSearchAgentsQueryParams): string => {
  return `
    query {
      search(
        ${name ? `name: "${name}",` : ''}
        ${location ? `location: "${location}",` : ''}
        country: "${country}",
        sort: { name: "${SortType.Random}"},
        pagination: { size: ${PAGE_SIZE}, from: ${(page - 1) * PAGE_SIZE} },
        searchType: ${SearchType.AGENT},
        agentType : ${agentType},
        recaptchaToken: "${recaptchaToken}"
        filters: { isMilnet: ${isMilnet} }
      ) {
        count
        agents {
          id
          firstName
          lastName
          city
          state
          photo
          email
          phoneNumber
          bio
          license {
            locale
            number
            state
            primary
          }
        }
      }
    }
  `;
};

/**
 *  Search agents by name, location and country
 *
 * @param name - Agent name query
 * @param location - Location query accepts state/province code like ON, BC, MI, etc
 * @param country - country code CA or US
 * @param page - page number
 * @param session_id - is a unique value to pass to ElasticSearch as a 'preference',
 * this ensures all subsequent requests get routed to the same shard. This is most important for paginated requests which
 * were sorting results differently across shards.
 * @returns
 *
 * Sort:
 *  Field name to sort by. Currently only firstName and lastName are 'officially' supported. Elasticsearch might allow other
 *  fields but they shouldn't be expected to always work. If you need a field to be sortable reach out to the middleware team.
 */

interface AgentSearchProps {
  name: string;
  location: string;
  country: string;
  page: number;
  session_id: string | null;
  agentType: AgentType;
  filters: AgentFilters;
}

const defaultFilters: AgentFilters = {
  isMilnet: false,
};

const searchAgents = async ({ name, location, country, page, session_id, agentType = AgentType.RESIDENTIAL, filters = defaultFilters }: AgentSearchProps): Promise<AgentsResponseBody | Error> => {
  const token = await generateRecaptchaToken(TokenActionType.AGENT_SEARCH);
  if (!token) {
    // Return empty results for graceful UX degradation
    // reCAPTCHA failure is tracked within generateRecaptchaToken
    return { count: 0, agents: []};
  }

  const query = buildSearchAgentsQuery({
    name,
    location,
    country,
    page,
    agentType,
    isMilnet: filters.isMilnet,
    recaptchaToken: token,
  });

  const params = {
    query,
    headers: session_id ? { 'X-Search-ID': session_id } : null,
  };

  const response = await agentEndpoint(params) as SearchAgentsResponse | Error;
  if (response instanceof Error) {
    return {
      count: 0,
      agents: [],
    };
  }
  const { search } = response;
  return search;
};

/**
 * Get set or featured agents based on the country
 *
 * @param country - country code CA or US
 * @param searchType - search type AGENT or BROKER
 * @param filters - Additional filters like isMilnet flag
 * @returns AgentsResponseBody
 */

const featuredAgents = async (
  country: CountryCodeList,
  searchType = SearchType.AGENT,
  filters: AgentFilters = defaultFilters
): Promise<AgentsResponseBody | Error> => {
  const token = await generateRecaptchaToken(TokenActionType.FEATURED_AGENTS);
  
  // Validate token before making API call
  if (!token) {
    console.error('Failed to generate reCAPTCHA token for featured agents');
    return { count: 0, agents: []};
  }
  
  const featuredAgentsQuery = `
    query {
      featuredAgents(
        country: "${country}"
        searchType: ${searchType}
        recaptchaToken: "${token}"
        filters: { isMilnet: ${filters.isMilnet} }
      ) {
        count
        agents {
          id
          firstName
          lastName
          city
          state
          photo
          bio
          license {
            locale
            number
            state
            primary
          }
        }
      }
    }
  `;

  const params = {
    query: featuredAgentsQuery,
  };

  const response = await agentEndpoint(params) as FeaturedAgentsResponse | Error;
  if (response instanceof Error) {
    return {
      count: 0,
      agents: [],
    };
  }
  const { featuredAgents } = response;
  return featuredAgents;
};

/**
 * Fetches location suggestions from the agent directory API
 *
 * @param query - search query
 * @param country - country code CA or US
 * @returns
 */
const locationSuggestions = async (query: string, country: string): Promise<string[]> => {
  const locationSuggestionsQuery = `
  query {
    locationSuggestions(
      term: "${query}",
      country: "${country}"
    )
  }
`;

  const response = await agentEndpoint({ query: locationSuggestionsQuery }) as LocationSuggestionsResponse | Error;
  if (response instanceof Error) {
    console.error('Error fetching location suggestions', response);
    return [];
  }
  const { locationSuggestions } = response;
  return locationSuggestions || [];
};

/**
 * Interface for name suggestions parameters
 */
interface NameSuggestionsProps {
  query: string;
  country: string;
  agentType?: AgentType;
  filters?: AgentFilters;
}

/**
 * Fetch name suggestions from the agent directory API
 *
 * @param props - Object containing all parameters
 * @param props.query - Search query string
 * @param props.country - Country code CA or US
 * @param props.agentType - Type of agent (RESIDENTIAL or COMMERCIAL)
 * @param props.filters - Additional filters like isMilnet flag
 * @returns Array of suggested agent names or Error
 */
const nameSuggestions = async ({
  query,
  country,
  agentType = AgentType.RESIDENTIAL,
  filters = defaultFilters,
}: NameSuggestionsProps): Promise<string[] | Error> => {
  const token = await generateRecaptchaToken(TokenActionType.NAME_SUGGESTIONS);
  
  // Validate token before making API call
  if (!token) {
    console.error('Failed to generate reCAPTCHA token for name suggestions');
    return [];
  }
  
  const nameSuggestionsQuery = `
    query {
      nameSuggestions(
        term: "${query}",
        country: "${country}",
        agentType: ${agentType},
        recaptchaToken: "${token}",
        filters: { isMilnet: ${filters.isMilnet} }
      )
    }
  `;

  const response = await agentEndpoint({ query: nameSuggestionsQuery }) as NameSuggestionsResponse | Error;
  if (response instanceof Error) {
    console.error('Error fetching name suggestions', response);
    return [];
  }
  const { nameSuggestions } = response;
  return nameSuggestions;
};

/**
 * Fetch agent by id
 * Test agent ID - de1cb15c-471d-11ee-86df-f3cc451c09a0
 *
 * @param id Agent ID
 * @returns
 */
const fetchAgent = async (id: string): Promise<AgentFull | Error> => {
  const token = await generateRecaptchaToken(TokenActionType.VIEW_AGENT);
  
  // Validate token before making API call
  if (!token) {
    console.error('Failed to generate reCAPTCHA token for fetchAgent');
    return new Error('Unable to generate reCAPTCHA token');
  }
  
  const query = `
    query {
      agent(
        id: "${id}",
        recaptchaToken: "${token}"
      ) {
        id
        firstName
        lastName
        email
        phoneNumber
        photo
        preferredName
        title
        bio
        languages
        specializations
        designations
        certifications
        facebook
        instagram
        linkedIn
        twitter
        website
        youtube
        tiktok
        city
        state
        zipcode
        countryCode
        asab
        stateBroker
        milnet {
          milnetAssociation
          status
        }
        activeLocations {
          city
          state
          zipcode
        }
        license {
          locale
          number
          state
          primary
        }
        isOffboarding
      }
    }
  `;

  const response = await agentEndpoint({ query }) as FullAgentResponse | Error;
  if (response instanceof Error) {
    console.error('Error fetching agent', response);
    return response;
  }

  const { agent } = response;
  return agent;
};

/**
 * Fetch total number of agents as well as agents count by country
 * @returns AgentCountBody
 */
const agentCount = async () => {
  // Brokers ca be added to the params if needed in the future
  const query = `
    query {
      agentCounts() {
        total
        countries {
          ca
          us
        }
      }
    }
  `;

  const response = await agentEndpoint({ query }) as AgentCountResponse | Error;
  if (response instanceof Error) {
    console.error('Error fetching agent count', response);
    return { total: 0, countries: { ca: '0', us: '0' }};
  }

  const { total, countries } = response.agentCounts;
  return { total, countries: { ca: formatNumberDown(countries.ca), us: formatNumberDown(countries.us) }};
};


const fetchAgentDetailsByNumericKey = async (numericKey: number) => {
  const token = await generateRecaptchaToken(TokenActionType.VIEW_AGENT);
  
  // Validate token before making API call
  if (!token) {
    console.error('Failed to generate reCAPTCHA token for fetchAgentDetailsByNumericKey');
    throw new Error('Unable to generate reCAPTCHA token');
  }
  
  const query = `
    query {
      agentByNumericKey(
        numericKey: ${numericKey},
        recaptchaToken: "${token}"
      ) {
        id
        photo
        phoneNumber
        cityState
        email
        firstName
        lastName
        milnet {
          milnetAssociation
          status
        }
        __typename
      }
    }
  `;

  const params = {
    query,
    headers: {
      'X-Exp-Api-Key': process.env.NEXT_PUBLIC_AGENT_DIR_API_KEY,
    },
  };

  const response = await agentEndpoint(params);
  if (response instanceof Error) {
    console.error('Error fetching agents by numeric key', response);
    throw response;
  }
  return response.agentByNumericKey;
};

/**
 *
 * @param params
 * @returns
 */

const agentEndpoint = async (params: AgentEndpointParams): Promise<Record<string, any> | Error> => {
  const { query, headers } = params;
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headers,
    },
    body: JSON.stringify({ query }),
  };
  try {
    const response = await fetch(configJSON.agentSearchUrl, options);
    const data = await response.json();

    if (data.errors) {
      console.error('Error fetching agents', data);
      throw new Error(data.errors[0].message);
    }

    return data.data;
  } catch (error: any) {
    if (error.message === 'Failed to fetch') {
      return new Error('Error fetching agents');
    }

    if (error.message === 'Unauthorized') {
      return new Error('Unauthorized');
    }

    if (error.message === 'Forbidden') {
      return new Error('Forbidden');
    }

    return new Error('Error fetching agents');
  }
};

// In order to generate a recaptcha token, the recaptcha script must be loaded on the page that calls this function.
// Right now this is only implemented in agent search page /pages/agents/index.tsx

/**
 * Configuration options for reCAPTCHA token generation.
 */
export interface RecaptchaApiConfigOptions {
  /** Maximum number of retry attempts */
  maxRetries: number;
  /** Timeout in milliseconds */
  timeout: number;
}

/**
 * Gets the default configuration options for reCAPTCHA API calls.
 * 
 * @returns Default reCAPTCHA configuration with maxRetries and timeout values
 */
export function getRecaptchaApiConfigOptions(): RecaptchaApiConfigOptions {
  return {
    maxRetries: RECAPTCHA_MAX_RETRIES,
    timeout: ApiTimeouts.RECAPTCHA_TOKEN_GENERATION,
  };
}

/**
 * Options for generating a reCAPTCHA token.
 */
interface GenerateRecaptchaTokenOptions {
  /** Maximum number of retry attempts. If not provided, uses default from getRecaptchaApiConfigOptions() */
  maxRetries?: number;
  /** Timeout in milliseconds. If not provided, uses default from getRecaptchaApiConfigOptions() */
  timeout?: number;
}

const generateRecaptchaToken = (
  action: TokenActionType,
  options: GenerateRecaptchaTokenOptions = {}
): Promise<string | null> => {
  const defaultConfig = getRecaptchaApiConfigOptions();
  const { maxRetries = defaultConfig.maxRetries, timeout = defaultConfig.timeout } = options;

  return new Promise(resolve => {
    let retryCount = 0;

    const trackFailure = (error: Error | null) => {
      try {
        AgentObservabilityService.trackRecaptchaTokenFailure({
          error: error || createRecaptchaError.generationFailed(),
        });
      } catch {
        // Silently fail - observability should never break the app
      }
    };
    
    const attemptTokenGeneration = () => {
      // Add timeout to prevent infinite waiting
      const timeoutId = setTimeout(() => {
        console.error(`reCAPTCHA token generation timeout after ${timeout}ms`);
        trackFailure(createRecaptchaError.timeout(timeout));
        resolve(null);
      }, timeout);

      if (window && window.grecaptcha && window.grecaptcha.enterprise) {
        window.grecaptcha.enterprise.ready(async () => {
          clearTimeout(timeoutId);
          try {
            // Validate that the reCAPTCHA key exists
            if (!process.env.NEXT_PUBLIC_RECAPTCHA_KEY) {
              console.error('reCAPTCHA key not configured');
              trackFailure(createRecaptchaError.keyNotConfigured());
              resolve(null);
              return;
            }

            const token = await window.grecaptcha.enterprise.execute(process.env.NEXT_PUBLIC_RECAPTCHA_KEY, { action });
            
            // Validate token format (basic check)
            if (typeof token === 'string' && token.length > 20) {
              resolve(token);
            } else {
              throw createRecaptchaError.invalidTokenFormat();
            }
          } catch (error) {
            console.error(`Error generating recaptcha token (attempt ${retryCount + 1}): `, error);
            
            // Retry logic
            if (retryCount < maxRetries) {
              retryCount++;
              // console.log(`Retrying token generation (${retryCount}/${maxRetries})...`);
              setTimeout(attemptTokenGeneration, 1000 * retryCount); // Exponential backoff
            } else {
              console.error('Max retries reached for reCAPTCHA token generation');
              trackFailure(error instanceof Error ? error : new Error(String(error)));
              resolve(null);
            }
          }
        });
      } else {
        clearTimeout(timeoutId);
        console.error('reCAPTCHA library not loaded yet.');
        
        // Retry if library not ready
        if (retryCount < maxRetries) {
          retryCount++;
          // console.log(`Waiting for reCAPTCHA library (${retryCount}/${maxRetries})...`);
          setTimeout(attemptTokenGeneration, 1000);
        } else {
          console.error('reCAPTCHA library failed to load after max retries');
          trackFailure(createRecaptchaError.libraryLoadFailed());
          resolve(null);
        }
      }
    };

    attemptTokenGeneration();
  });
};

/**
 * Minimal agent photo data returned by searchAgentPhotos.
 */
export interface AgentPhoto {
  id: string;
  firstName: string;
  lastName: string;
  photo: string;
}

interface AgentPhotoSearchResponse {
  search: {
    agents: AgentPhoto[];
  };
}

/**
 * Fetch agent photos for a given location. Returns only agents that have a
 * photo set, up to `limit` results. Uses a minimal GraphQL selection to keep
 * the payload small.
 *
 * @param location - City / province / state name
 * @param country  - Country code (CA or US)
 * @param limit    - Maximum number of agent photos to return (default 4)
 */
const searchAgentPhotos = async (
  location: string,
  country: string,
  limit = 4
): Promise<AgentPhoto[]> => {
  const token = await generateRecaptchaToken(TokenActionType.AGENT_SEARCH);
  if (!token) {
    return [];
  }

  // Request extra agents so we can filter out those without photos
  const fetchSize = limit * 3;

  const query = `
    query {
      search(
        location: "${location}",
        country: "${country}",
        sort: { name: "${SortType.Random}" },
        pagination: { size: ${fetchSize}, from: 16 },
        searchType: ${SearchType.AGENT},
        agentType: ${AgentType.RESIDENTIAL},
        recaptchaToken: "${token}"
        filters: { isMilnet: false }
      ) {
        agents {
          id
          firstName
          lastName
          photo
        }
      }
    }
  `;

  const response = await agentEndpoint({ query }) as AgentPhotoSearchResponse | Error;
  if (response instanceof Error) {
    return [];
  }

  const agents = response.search?.agents ?? [];
  return agents
    .filter(agent => agent.photo && agent.photo.length > 0)
    .slice(0, limit);
};

export {
  searchAgents,
  locationSuggestions,
  nameSuggestions,
  featuredAgents,
  fetchAgent,
  fetchAgentDetailsByNumericKey,
  agentCount,
  searchAgentPhotos,
};
